import type {
    DealOpportunityProductAdder,
    ProductTypeAdder,
} from '../../../../../../services/oneButtonServices/quoteServices';

export const formatAdderPriceDisplay = (total: number | null | undefined): string => {
    if (total == null || !Number.isFinite(total) || total <= 0) return '—';
    return `$${total.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const normalizeProductTypeName = (value: unknown): string => String(value ?? '').trim().toLowerCase();

export const productTypeMatchesAdder = (apiProductType: unknown, selectedProductType: string): boolean => {
    const api = normalizeProductTypeName(apiProductType);
    const selected = normalizeProductTypeName(selectedProductType);
    if (selected === 'r&r') return api === 'r&r' || api === 'rr';
    return api === selected;
};

/** Same line-total rules as AddersModal (`chargeable qty × unit price`). */
export function calculateAdderLineTotal(
    qty: number,
    unitPrice: number,
    freeQuantity = 0,
): number {
    const chargeableQty = Math.max(0, Math.floor(Number(qty) || 0) - (Number(freeQuantity) || 0));
    return chargeableQty * (Number(unitPrice) || 0);
}

/** Sum saved deal adders for one product tab (Solar / Roof / Battery). */
export function sumAdderPriceForProductType(
    productType: string,
    opportunityProductAdders: DealOpportunityProductAdder[] | null | undefined,
    productTypeAdders: ProductTypeAdder[] | null | undefined,
): number {
    if (!productType?.trim()) return 0;

    const options = (productTypeAdders ?? [])
        .filter((adder) => adder?.Active_Ind !== false)
        .filter((adder) => productTypeMatchesAdder(adder?.Product_Type, productType))
        .map((adder) => ({
            productTypeAdderId: Number(adder.Product_Type_Adder_Id ?? 0),
            productTypeId: Number(adder.Product_Type_Id ?? 0),
            unitPrice: Number(adder.Unit_Price ?? 0),
            freeQuantity: Number(adder.Free_Quantity ?? 0),
        }))
        .filter((option) => option.productTypeAdderId > 0);

    const productTypeIds = new Set(options.map((option) => option.productTypeId).filter(Boolean));
    if (!productTypeIds.size || !opportunityProductAdders?.length) return 0;

    return opportunityProductAdders
        .filter((adder) => adder?.Active_Ind !== false)
        .filter((adder) => productTypeIds.has(Number(adder.Product_Type_Id ?? 0)))
        .reduce((sum, adder) => {
            const option = options.find(
                (opt) => opt.productTypeAdderId === Number(adder.Product_Type_Adder_Id ?? 0),
            );
            return (
                sum +
                calculateAdderLineTotal(
                    Number(adder.Quantity ?? 0),
                    Number(adder.UnitPrice ?? option?.unitPrice ?? 0),
                    option?.freeQuantity ?? 0,
                )
            );
        }, 0);
}
